import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_REPORT_TEMPLATE_STATUSES,
  REPORT_TEMPLATE_LAYOUT,
  REPORT_TEMPLATE_STATUS,
  type ReportTemplateStatus,
} from '@lims/shared-types';
import { reportTemplatesApi, type ReportTemplateView } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  SelectField,
  type DataColumn,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import {
  LanguageList,
  TEMPLATES_QUERY_KEY,
  TemplateBadges,
  TemplateFormDialog,
  TemplateManageActions,
  templateName,
} from './report-template-parts.js';
import { RegisterPanel } from '../reports/register-panel.js';

type StatusFilter = ReportTemplateStatus | 'all';

/**
 * The report-template register — Phase 10, decision D112.
 *
 * IT STARTS EMPTY. No template is seeded by migration, seed or deployment: a
 * report layout is a laboratory's own configuration, and the empty state says
 * that the manager creates the first one.
 *
 * Reading needs `templates.view`; every change needs `templates.manage` and is
 * re-checked by the server whatever this page shows. Withdrawing a template is
 * terminal and never alters a report already issued on it — the count of
 * issued versions sits on every row so a manager knows before deciding.
 */
export function ReportTemplatesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: [...TEMPLATES_QUERY_KEY, 'list', { page, status }],
    queryFn: () =>
      reportTemplatesApi.list({ page, ...(status === 'all' ? {} : { status }) }),
  });

  const mayManage = can(PERMISSIONS.TEMPLATES_MANAGE);
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;

  const createButton = mayManage ? (
    <ActionButton priority="primary" onPress={() => setCreating(true)}>
      {t('reportTemplates.create')}
    </ActionButton>
  ) : null;

  /*
    Column one carries the name, its state and the link that opens it, so on a
    phone — where the table scrolls sideways — the row's key facts and its
    action are the part already on screen.
  */
  const columns: readonly DataColumn<ReportTemplateView>[] = [
    {
      key: 'name',
      header: t('reportTemplates.columnName'),
      render: (template) => {
        const primary = templateName(template, locale);
        const secondary = locale === 'ar' ? template.name.en : template.name.ar;
        return (
          <div className="flex min-w-0 flex-col gap-1">
            <Link
              to={`/report-templates/${template.id}`}
              className="font-medium text-primary-700 underline-offset-2 hover:underline"
            >
              <bdi>{primary}</bdi>
            </Link>
            {secondary && secondary !== primary ? (
              <span className="block truncate text-caption text-default-600">
                <bdi>{secondary}</bdi>
              </span>
            ) : null}
            <TemplateBadges template={template} />
          </div>
        );
      },
    },
    {
      key: 'languages',
      header: t('reportTemplates.columnLanguages'),
      render: (template) => <LanguageList languages={template.languages} />,
    },
    {
      key: 'issued',
      header: t('reportTemplates.columnIssued'),
      align: 'end',
      render: (template) => (
        <span className="tabular-nums text-default-800">{template.issuedVersionCount}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (template) => (
        <div className="flex flex-col items-end gap-inline">
          {/* The template's page holds its section layout and A4 preview. A
              withdrawn template offers no action; its name still opens it. */}
          {template.status === REPORT_TEMPLATE_STATUS.ACTIVE ? (
            <ActionButton
              priority="secondary"
              size="sm"
              onPress={() => navigate(`/report-templates/${template.id}`)}
            >
              {t('reportTemplates.openDesign')}
            </ActionButton>
          ) : null}
          <TemplateManageActions template={template} />
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: 'all', label: t('common.all') },
    ...ALL_REPORT_TEMPLATE_STATUSES.map((value) => ({
      value,
      label: t(`reportTemplateStatus.${value}`),
    })),
  ];

  /** Nothing at all exists yet — not "nothing matches the filter". */
  const registerIsEmpty = status === 'all' && list.isSuccess && total === 0;
  /** Only claimed when the answer covers every template (one page, no filter). */
  const noActiveTemplate =
    list.isSuccess &&
    ((status === REPORT_TEMPLATE_STATUS.ACTIVE && total === 0) ||
      (status === 'all' &&
        total > 0 &&
        (list.data.meta.totalPages ?? 1) <= 1 &&
        !rows.some((row) => row.status === REPORT_TEMPLATE_STATUS.ACTIVE)));

  return (
    <>
      <PageHeader
        title={t('reportTemplates.title')}
        subtitle={t('reportTemplates.subtitle')}
        breadcrumbs={[{ label: t('nav.reportTemplates') }]}
        actions={createButton}
      />

      <div className="flex flex-col gap-gutter">
        <Alert
          tone="info"
          title={t(`reportTemplateLayout.${REPORT_TEMPLATE_LAYOUT.GENERAL}`)}
          description={t('reportTemplates.layoutHint')}
        />

        {noActiveTemplate ? (
          <Alert tone="warning" title={t('reportTemplates.noneActive')} />
        ) : null}

        <RegisterPanel
          toolbar={
            <FilterBar>
              <div className="w-full sm:w-64">
                <SelectField
                  label={t('reportTemplates.filterStatus')}
                  options={statusOptions}
                  value={status}
                  onChange={(value) => {
                    setStatus((value as StatusFilter | null) ?? 'all');
                    setPage(1);
                  }}
                />
              </div>
            </FilterBar>
          }
        >
          {list.isLoading ? (
            <LoadingState />
          ) : list.error ? (
            <div className="p-4">
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            </div>
          ) : registerIsEmpty ? (
            <div className="p-4">
              <EmptyState
                title={t('reportTemplates.empty')}
                description={t('reportTemplates.emptyHint')}
                action={createButton}
              />
            </div>
          ) : (
            <DataTable
              label={t('reportTemplates.title')}
              columns={columns}
              rows={rows}
              rowKey={(template) => template.id}
              emptyContent={t('reportTemplates.emptyFiltered')}
              page={page}
              totalPages={list.data?.meta.totalPages ?? 1}
              onPageChange={setPage}
            />
          )}
        </RegisterPanel>
      </div>

      {creating ? <TemplateFormDialog template={null} onClose={() => setCreating(false)} /> : null}
    </>
  );
}
